import { ProgressState } from '../types/progress';

const PROGRESS_FILE_NAME = 'algoflow_progress.json';
const PROGRESS_FILE_MIME = 'application/json';

export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private accessToken: string | null = null;

  private constructor() {}

  static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      GoogleDriveService.instance = new GoogleDriveService();
    }
    return GoogleDriveService.instance;
  }

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  private async findProgressFile(): Promise<string | null> {
    try {
      const response = await fetch(
        'https://www.googleapis.com/drive/v3/files?q=name=\'' + PROGRESS_FILE_NAME + '\' and trashed=false',
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      const data = await response.json();
      if (data.files && data.files.length > 0) {
        return data.files[0].id;
      }
      return null;
    } catch (error) {
      console.error('Error finding progress file:', error);
      return null;
    }
  }

  private async createProgressFile(content: ProgressState): Promise<string | null> {
    try {
      // Create file metadata
      const metadata = {
        name: PROGRESS_FILE_NAME,
        mimeType: PROGRESS_FILE_MIME,
      };

      // Create multipart request body
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([JSON.stringify(content)], { type: PROGRESS_FILE_MIME }));

      const response = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          },
          body: form
        }
      );

      const data = await response.json();
      return data.id;
    } catch (error) {
      console.error('Error creating progress file:', error);
      return null;
    }
  }

  async saveProgress(progress: ProgressState): Promise<boolean> {
    if (!this.accessToken) {
      console.error('No access token available');
      return false;
    }

    try {
      const fileId = await this.findProgressFile();

      if (fileId) {
        // Update existing file
        const form = new FormData();
        form.append('file', new Blob([JSON.stringify(progress)], { type: PROGRESS_FILE_MIME }));

        await fetch(
          `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
            },
            body: form
          }
        );
      } else {
        // Create new file
        await this.createProgressFile(progress);
      }

      return true;
    } catch (error) {
      console.error('Error saving progress:', error);
      return false;
    }
  }

  async loadProgress(): Promise<ProgressState | null> {
    if (!this.accessToken) {
      console.error('No access token available');
      return null;
    }

    try {
      const fileId = await this.findProgressFile();
      if (!fileId) return null;

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      return await response.json();
    } catch (error) {
      console.error('Error loading progress:', error);
      return null;
    }
  }

  async deleteProgress(): Promise<boolean> {
    if (!this.accessToken) {
      console.error('No access token available');
      return false;
    }

    try {
      const fileId = await this.findProgressFile();
      if (!fileId) return true;

      await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        }
      );

      return true;
    } catch (error) {
      console.error('Error deleting progress:', error);
      return false;
    }
  }
}
